// Client-only split-screen orchestration. No HTTP — mirrors the `maps` mock store.
// `tiles` is the single source of truth for what's on the canvas: a map is visible
// iff it's a tile. The eye toggle and the directional Split submenu both mutate it.
import { defineStore } from 'pinia'
import { toast } from '@/components/ui/toast/use-toast'
import { useMapsStore } from '@/stores/maps'

type OrgId = number | string
type MapId = string
export type Arrangement = 'lr' | 'tb' | 'quad' | 'left' | 'right' | 'top' | 'bottom'
export type Direction = 'left' | 'right' | 'top' | 'bottom'

const MAX_TILES = 4
const EDGE_FIRST = new Set(['left', 'top'])   // placed map takes the first slot
const VERTICAL = new Set(['top', 'bottom'])   // top/bottom => stacked (arrangement 'tb')

// Default arrangement for an auto-tiled grid of `count` maps (no explicit direction).
const arrangementFor = (count: number): Arrangement => (count >= 4 ? 'quad' : count === 3 ? 'left' : 'lr')

const tooManyToast = () => toast({ title: 'You can show up to 4 maps at once' })

interface SplitState {
  // Split-screen state, keyed by orgId. Client-only (resets on reload).
  // `tiles` is the single source of truth for which maps are on the canvas: a
  // map is visible iff it's a tile. A split is active when `tiles` has >1 map;
  // the arrangement decides the grid.
  tiles: Record<string, MapId[]>              // ordered by slot, len <= 4
  arrangement: Record<string, Arrangement>
  dividers: Record<string, { x: number, y: number }> // divider fractions in [0.1, 0.9]
  // Guards the one-time default-map seed so "user hid everything" (tiles empty)
  // isn't mistaken for "not yet initialized" and re-seeded.
  initialized: Record<string, boolean>
  // Per-map (keyed by mapId, not orgId): camera sync participation (default true)
  syncEnabled: Record<string, boolean>
  // Which tile global map interactions should target (e.g. layer-create drawing).
  // Transient: set while a flow that targets a specific map is open, null otherwise.
  focusedTile: MapId | null
}

export const useSplitStore = defineStore('split', {
  state: (): SplitState => ({
    tiles: {},
    arrangement: {},
    dividers: {},
    initialized: {},
    syncEnabled: {},
    focusedTile: null,
  }),

  getters: {
    getTiles: state => (orgId: OrgId): MapId[] => state.tiles[orgId] || [],

    getArrangement: state => (orgId: OrgId): Arrangement => state.arrangement[orgId] || 'lr',

    getDividers: state => (orgId: OrgId) => state.dividers[orgId] || { x: 0.5, y: 0.5 },

    isSplitActive: state => (orgId: OrgId) => (state.tiles[orgId] || []).length > 1,

    // A map is visible iff it's currently a tile.
    isVisible: state => (orgId: OrgId, mapId: MapId) => (state.tiles[orgId] || []).includes(mapId),

    isInitialized: state => (orgId: OrgId) => state.initialized[orgId] === true,

    // Default true when unset so newly-added tiles start synced.
    isSyncEnabled: state => (mapId: MapId) => state.syncEnabled[mapId] !== false,
  },

  actions: {
    setTiles({ orgId, tiles }: { orgId: OrgId, tiles: MapId[] }) {
      this.tiles = { ...this.tiles, [orgId]: [...new Set(tiles)].slice(0, MAX_TILES) }
    },

    setArrangement({ orgId, arrangement }: { orgId: OrgId, arrangement: Arrangement }) {
      this.arrangement = { ...this.arrangement, [orgId]: arrangement }
    },

    setDividers({ orgId, x, y }: { orgId: OrgId, x?: number | null, y?: number | null }) {
      const prev = this.dividers[orgId] || { x: 0.5, y: 0.5 }
      const clamp = (v: number) => Math.max(0.1, Math.min(0.9, v))
      this.dividers = {
        ...this.dividers,
        [orgId]: { x: x != null ? clamp(x) : prev.x, y: y != null ? clamp(y) : prev.y }
      }
    },

    setSync({ mapId, enabled }: { mapId: MapId, enabled: boolean }) {
      this.syncEnabled = { ...this.syncEnabled, [mapId]: enabled }
    },

    setInitialized({ orgId }: { orgId: OrgId }) {
      this.initialized = { ...this.initialized, [orgId]: true }
    },

    // Seed the canvas with the Default Map once, so the first map is visible by default.
    // The `initialized` guard means a user who hides every map keeps a blank canvas
    // instead of the default reappearing.
    ensureTiles({ orgId }: { orgId: OrgId }) {
      if (this.isInitialized(orgId)) return
      const def = useMapsStore().getDefaultMap(orgId)
      if (!def) return
      this.setTiles({ orgId, tiles: [def.id] })
      this.setInitialized({ orgId })
    },

    // Add a map to the canvas (eye on). Auto-tiles with a sensible default arrangement.
    showMap({ orgId, mapId }: { orgId: OrgId, mapId: MapId }) {
      const tiles = this.getTiles(orgId)
      if (tiles.includes(mapId)) return
      if (tiles.length >= MAX_TILES) { tooManyToast(); return }
      const newTiles = [...tiles, mapId]
      this.setArrangement({ orgId, arrangement: arrangementFor(newTiles.length) })
      this.setTiles({ orgId, tiles: newTiles })
    },

    // Remove a map from the canvas (eye off / tile ✕). Unlike the old collapse, a single
    // remaining map stays as one tile, and zero maps leaves a blank canvas.
    hideMap({ orgId, mapId }: { orgId: OrgId, mapId: MapId }) {
      const remaining = this.getTiles(orgId).filter(id => id !== mapId)
      if (remaining.length === 2) {
        const arr = this.getArrangement(orgId)
        this.setArrangement({ orgId, arrangement: arr === 'tb' ? 'tb' : 'lr' })
      }
      this.setTiles({ orgId, tiles: remaining })
    },

    toggleMap({ orgId, mapId }: { orgId: OrgId, mapId: MapId }) {
      if (this.getTiles(orgId).includes(mapId)) this.hideMap({ orgId, mapId })
      else this.showMap({ orgId, mapId })
    },

    // ✕ on a tile — same as turning its eye off.
    removeTileMap({ orgId, mapId }: { orgId: OrgId, mapId: MapId }) {
      this.hideMap({ orgId, mapId })
    },

    // Place a map into the split from its sidebar menu, at the slot implied by direction.
    //  - direction picks the arrangement AND which slot the placed map takes.
    //  - Adding to a single tile makes a 2-up; a 3rd becomes a "master" layout where the
    //    placed map takes the full edge; a 4th switches to a 2×2 quad.
    placeInSplit({ orgId, mapId, direction }: { orgId: OrgId, mapId: MapId, direction: Direction }) {
      const tiles = this.getTiles(orgId)
      if (tiles.length >= MAX_TILES && !tiles.includes(mapId)) { tooManyToast(); return }
      const others = tiles.filter(id => id !== mapId)
      const first = EDGE_FIRST.has(direction)

      if (others.length === 0) {
        // Nothing else on the canvas yet — just show this map full-bleed.
        this.setTiles({ orgId, tiles: [mapId] })
        return
      }

      let arrangement: Arrangement
      let newTiles: MapId[]
      if (others.length === 1) {
        arrangement = VERTICAL.has(direction) ? 'tb' : 'lr'
        newTiles = first ? [mapId, others[0]] : [others[0], mapId]
      } else if (others.length === 2) {
        // 3-up master: the direction is the arrangement; placed map is the master.
        arrangement = direction
        newTiles = first ? [mapId, ...others] : [...others, mapId]
      } else {
        arrangement = 'quad'
        newTiles = (first ? [mapId, ...others] : [...others, mapId]).slice(0, MAX_TILES)
      }

      this.setArrangement({ orgId, arrangement })
      this.setTiles({ orgId, tiles: newTiles })
    },

    toggleSync({ mapId }: { mapId: MapId }) {
      this.setSync({ mapId, enabled: !this.isSyncEnabled(mapId) })
    },

    // Focus a tile so global map interactions (layer-create drawing, search) target it.
    setFocusedTile({ mapId }: { mapId?: MapId | null }) {
      this.focusedTile = mapId || null
    },
  },
})
